import { useEffect, useState } from 'react'
import s from './DocFolders.scss'
import { Table } from 'reactstrap'
import { makeGetRequest, makePutRequest, makePostRequest, makeDeleteRequest } from 'utils/api'
import { useLoading } from 'hooks'
import { Checkbox, AddOptions } from 'components/ideal-core-ui'
import { useDispatch } from 'react-redux'
import { toCategories, toCategory } from './serializers'
import swal from 'sweetalert'

const DocFolders = () => {
  const [, withLoadingLocks] = useLoading()
  const [defaultFolders, setDefaultFolders] = useState([])
  const [customFolders, setCustomFolders] = useState([])
  const dispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      const response = await withLoadingLocks(makeGetRequest('/v1/folder'))
      setDefaultFolders(response.filter(folder => folder.system))
      setCustomFolders(response.filter(folder => !folder.system))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateVisibility = async (folder, visible) => {
    await withLoadingLocks(
      makePutRequest(`/v1/folder/${folder.id}`, {
        visible,
        name: folder.name
      })
    )

    dispatch({
      type: 'PUSH_NOTIFICATION',
      payload: {
        title: 'Visibility updated successfully',
        level: 'success'
      }
    })

    setDefaultFolders(
      defaultFolders.map(f => {
        if (f.id === folder.id) {
          return { ...f, visible }
        }
        return f
      })
    )
  }

  const addFolder = async (folders, name) => {
    const response = await withLoadingLocks(
      makePostRequest('/v1/folder', {
        name
      })
    )

    dispatch({
      type: 'PUSH_NOTIFICATION',
      payload: {
        title: 'Folder added successfully',
        level: 'success'
      }
    })

    setCustomFolders([...customFolders, response])
  }

  const editFolder = async (id, name) => {
    makePutRequest(`/v1/folder/${id}`, {
      name
    })

    dispatch({
      type: 'PUSH_NOTIFICATION',
      payload: {
        title: 'Folder updated successfully',
        level: 'success'
      }
    })

    setCustomFolders(
      customFolders.map(folder => {
        if (folder.id === id) {
          return { ...folder, name }
        }
        return folder
      })
    )
  }

  const deleteFolder = async folder => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this folder?',
      text: 'All documents in this folder will be moved to "Uncategorized" folder. This action can’t be undone',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        makeDeleteRequest(`/v1/folder/${folder.value}`, {
          name
        })

        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Folder deleted successfully',
            level: 'success'
          }
        })

        setCustomFolders(customFolders.filter(f => f.id !== folder.value))
      } catch (e) {
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'There was a problem deleting folder',
            level: 'error'
          }
        })
      }
    }
  }

  return (
    <>
      <h5>System Folders</h5>
      <p>
        The following folders for opportunity documents are available by default. You can uncheck
        the &quot;Visibility&quot; checkbox if you want to hide them.
      </p>
      <Table style={{ marginBottom: 50 }}>
        <thead>
          <tr>
            <th>Folder Name</th>
            <th>Number of Files</th>
            <th>Visibility in Docs</th>
          </tr>
        </thead>
        <tbody>
          {defaultFolders.map(folder => (
            <tr key={folder.id}>
              <td>{folder.name}</td>
              <td>{folder.documentsCount ?? 0}</td>
              <td>
                <Checkbox
                  size="md"
                  className={s.checkbox}
                  isChecked={folder.visible}
                  triggerCheckbox={value => {
                    updateVisibility(folder, value.target.checked)
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <AddOptions
        texts={{
          title: 'Custom Folders',
          description:
            'You can define custom folders here. They will appear in every opportunity on the Docs Tab'
        }}
        allowEmpty
        items={toCategories(customFolders)}
        toItemSerializer={toCategory}
        addItem={addFolder}
        editItem={editFolder}
        deleteItem={deleteFolder}
      />
    </>
  )
}

export default DocFolders
